// The system states (docs/DESIGN.md, "Loading, empty, and errors"), as shared components. Each response has its own
// copy, and none falls back to a generic error: "not loaded yet", "refused" and "not yours" are
// different answers on purpose, and a screen that shows them the same way teaches people that
// every one of them is a breakage.
//
// Full-page states are centred text, not red boxes, with the status code in small monospace at
// the top so support can recognise it. This file holds their exact wording.

import type { ReactNode } from "react";

export function StatePage({ code, title, children, actions }:
    { code: string; title: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="state-wrap">
      <div className="state-page" role="alert">
        <span className="state-code">{code}</span>
        <h1>{title}</h1>
        {children}
        {actions && <div className="state-actions">{actions}</div>}
      </div>
    </div>
  );
}

/**
 * 403 on a Student Learning read: sign-in was refused after all (the account was disabled, or is
 * no longer let in, since the page loaded). The server's own sentence says why and who to ask.
 *
 * NOT "you have no classes". Someone who reaches no class is let in and gets empty lists: what a
 * person reaches is their relationships (docs/ACCESS.md), and there is no class gate to refuse.
 */
export function RefusedState({ detail }: { detail?: string | null }) {
  return (
    <StatePage code="403" title="Your sign-in was refused">
      <p>{detail || "This account isn't let in. Ask your organization's administrator."}</p>
    </StatePage>
  );
}

/**
 * 404 for something in a cohort you don't teach. Row-level security hides it, so to this caller
 * it doesn't exist — and the page never says "forbidden" or "access denied", which would confirm
 * that it does.
 */
export function NotFoundState({ what, action }: { what: string; action?: ReactNode }) {
  return (
    <StatePage code="404" title={`We can't find that ${what}`} actions={action}>
      <p>It isn't in any of your cohorts. It may be in a cohort you don't teach, or the link may be
        wrong.</p>
    </StatePage>
  );
}

export function AdminOnlyState() {
  return (
    <StatePage code="admin" title="This page is for admins">
      <p>Your account doesn't show admin tools. If you think it should, ask your
        administrator.</p>
    </StatePage>
  );
}

export function ErrorState() {
  return (
    <StatePage code="error" title="Something went wrong on our side"
               actions={<button className="btn primary" onClick={() => window.location.reload()}>Reload</button>}>
      <p>The page didn't load. Reload to try again. If it keeps happening, the problem is ours, not
        your data.</p>
    </StatePage>
  );
}

/** Chat is given, not open to all (0089): in the chat column, for someone not given it. */
export function NoChatState() {
  return (
    <div className="empty" role="status">
      <svg className="i" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v11H8l-4 4z" /></svg>
      <h3>Chat isn't turned on for you</h3>
      <p>Each answer is an AI call, so chat is given to people rather than open to everyone. Your
        district's configurators can give it to you once Preva has turned chat on for your district.
        Everything else on this page works without it.</p>
    </div>
  );
}

/** `available: false` — information, not a warning. In the panel, under the page's own header. */
export function NotLoaded({ what }: { what: string }) {
  return (
    <div className="empty" role="status">
      <svg className="i" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <h3>{what} hasn't loaded for this cohort yet</h3>
      <p>Nothing is wrong. It appears here once the data arrives. You don't need to do anything.</p>
    </div>
  );
}

/**
 * A 409 on a teacher action: the paper moved, or the database refused. Inline at the top of the
 * panel where the action happened, and the caller keeps the person's unsaved input on screen.
 */
export function ConflictBanner({ frozenRead = false, onReload }: { frozenRead?: boolean; onReload: () => void }) {
  return (
    <div className="state-banner warn" role="alert">
      <svg className="i" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11a8 8 0 1 0-2.3 5.7M20 5v6h-6" /></svg>
      <div className="grow">
        <strong>Not saved.</strong>{" "}
        {frozenRead
          ? "This folder read is confirmed, so it can't change. To correct it, read the folder again."
          : "This moved while you were looking. Reload to see where it is now, then try again."}
      </div>
      <button className="btn sm" onClick={onReload}>Reload</button>
    </div>
  );
}

/** The all-or-nothing refusal of a set override, shown inside its dialog. */
export function SetOverrideConflict() {
  return (
    <div className="state-banner warn" role="alert">
      <div className="grow">
        <strong>Nothing changed.</strong> One of these papers moved while you were looking. It's all
        or nothing, so no paper was updated. Close this, reload the list, and try again.
      </div>
    </div>
  );
}

/** A skeleton shaped like the page: a title bar and table rows. Never a spinner for a page load. */
export function PageSkeleton({ rows = 7 }: { rows?: number }) {
  return (
    <div className="skel-page" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      <div className="skel" style={{ width: 120, marginBottom: 12 }} />
      <div className="skel" style={{ width: 320, height: 24, marginBottom: 28 }} />
      <div className="skel-panel">
        {Array.from({ length: rows }, (_, i) => (
          <div className="skel-row" key={i}>
            <div className="skel" style={{ width: `${22 + ((i * 13) % 20)}%` }} />
            <div className="skel" style={{ width: "12%" }} />
            <div className="skel" style={{ flex: 1 }} />
          </div>
        ))}
      </div>
    </div>
  );
}
